import type { UpdateProfileRequest, UserProfile } from '@lista/shared-validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';

export function useProfile(enabled = true) {
  const { services } = useCore();
  return useQuery({ queryKey: queryKeys.me, queryFn: services.users.me, enabled });
}

/** Optimistic profile update. `onSaved` lets the app sync its session (name, locale). */
export function useUpdateProfile(onSaved?: (profile: UserProfile) => void) {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProfileRequest) => services.users.update(body),
    onMutate: (body) => {
      const before = qc.getQueryData<UserProfile>(queryKeys.me);
      if (before) qc.setQueryData<UserProfile>(queryKeys.me, { ...before, ...body });
      return { before };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(queryKeys.me, ctx?.before),
    onSuccess: (profile) => {
      qc.setQueryData(queryKeys.me, profile);
      // The country and city decide which sponsored lists a user sees.
      void qc.invalidateQueries({ queryKey: queryKeys.sponsored });
      onSaved?.(profile);
    },
  });
}

/** UC-ACC-09. On success the app must sign out locally and drop its caches. */
export function useDeleteAccount() {
  const { services } = useCore();
  return useMutation({ mutationFn: (password: string) => services.users.deleteAccount(password) });
}

/** FR-USR-03. Resolves to the export document; saving it is the app's job. */
export function useExportData() {
  const { services } = useCore();
  return useMutation({ mutationFn: () => services.users.exportData() });
}

/** Profile: send the verification email again (rate limited by the server). */
export function useResendVerification() {
  const { services } = useCore();
  return useMutation({ mutationFn: () => services.auth.resendVerification() });
}
